import { useState } from "react";
import { Page, useCurrent, Grid } from "./common";
import { PageHead, Plate, Modal, Empty } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions } from "../lib/store";
import { storeImage } from "../lib/sync";
import { fileToDataUri } from "../lib/image";
import { useCan, useSession } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { DeckBuilder } from "./ConceptDeck";
import { FilmsPanel } from "./Films";
import { relative } from "../lib/format";
import type { ConceptBoard } from "../lib/types";

/* The concept stage in practice: put two or three directions on the table,
   show them to the client, and let them say which one they're drawn to. */
export default function Concepts() {
  const p = useCurrent();
  const nav = useNavigate();
  const me = useSession();
  const canEdit = useCan("brief");
  const [editing, setEditing] = useState<ConceptBoard | "new" | null>(null);
  const [view, setView] = useState<"directions" | "deck" | "film">("directions");
  if (!p) return null;

  const presented = !!p.conceptsPresentedOn;
  const picked = p.concepts.find((c) => c.response?.choice === "preferred");
  const deck = p.deck;

  return (
    <Page>
      <PageHead kicker={`${p.name} · Concept Design`} title={view === "deck" ? "Concept presentation" : view === "film" ? "Film" : "Conceptual direction"}>
        {view === "directions" && canEdit && <button className="btn ghost sm" onClick={() => setEditing("new")}><Icon.plus width={15} />Add a direction</button>}
        {view === "directions" && canEdit && p.concepts.length > 0 && (
          <button className="btn accent sm" onClick={() => actions.presentConcepts(p.id)}>
            <Icon.arrowUpRight width={15} />{presented ? "Re-issue to client" : "Present to client"}
          </button>
        )}
        {((view === "deck" && deck?.status === "issued") || (view === "film" && (p.films ?? []).some((f) => f.sharedWithClient))) && (
          <button className="btn ghost sm" onClick={() => nav(`/portal/${p.id}/${view === "film" ? "film" : "concept"}`)}><Icon.arrowUpRight width={15} />Client view</button>
        )}
      </PageHead>

      {/* two ways of working at concept stage: loose directions on a board, or
          the presentation you actually walk the client through */}
      <div className="row wrap reveal" style={{ gap: 6, marginBottom: 20 }}>
        {([["directions", "Directions"], ["deck", "Presentation & options"], ["film", "Film"]] as const).map(([k, label]) => (
          <button key={k} className="chip" onClick={() => setView(k)}
            style={{ fontSize: 12.5, padding: "6px 13px", background: view === k ? "var(--accent)" : "var(--card)", color: view === k ? "#fff" : "var(--ink-2)", borderColor: view === k ? "var(--accent)" : "var(--line-2)" }}>
            {label}{k === "deck" && deck ? ` · ${deck.options.length || deck.slides.length}` : ""}
            {k === "film" && (p.films ?? []).length ? ` · ${(p.films ?? []).length}` : ""}
          </button>
        ))}
      </div>

      {view === "film" ? (
        canEdit
          ? <FilmsPanel p={p} by={me?.name ?? "Studio"} />
          : <Empty>Only the design team can nest films in the portal.</Empty>
      ) : view === "deck" ? (
        canEdit
          ? <DeckBuilder p={p} by={me?.name ?? "Studio"} />
          : <Empty>Only the design team can craft the concept presentation.</Empty>
      ) : (
      <>
      <p className="muted reveal" style={{ maxWidth: 640, marginBottom: 22, fontSize: 16 }}>
        The narrative threads that shape {p.name} — explored as mood, material and plan before the scheme is resolved.
      </p>

      {p.concepts.length > 0 && (
        <div className="row wrap reveal" style={{ gap: 8, marginBottom: 20 }}>
          <span className="badge">{p.concepts.length} direction{p.concepts.length === 1 ? "" : "s"}</span>
          {presented
            ? <span className="badge ok"><span className="dot" />Presented {relative(p.conceptsPresentedOn)}</span>
            : <span className="badge warn"><span className="dot" />Not yet shown to the client</span>}
          {picked && <span className="badge accent"><Icon.check width={10} />Client prefers “{picked.title}”</span>}
        </div>
      )}

      {p.concepts.length === 0 ? (
        <Empty>
          No directions yet.{canEdit ? " Add the first one — a title, the thinking behind it, and an image if you have one." : ""}
          {canEdit && <div style={{ marginTop: 14 }}><button className="btn accent" onClick={() => setEditing("new")}><Icon.plus width={15} />Add a direction</button></div>}
        </Empty>
      ) : (
        <Grid cols="repeat(auto-fill, minmax(300px, 1fr))" gap={22}>
          {p.concepts.map((c, i) => {
            const pref = c.response?.choice === "preferred";
            const out = c.response?.choice === "no";
            return (
              <div key={c.id} className="card reveal" style={{ overflow: "hidden", display: "flex", flexDirection: "column", opacity: out ? 0.62 : 1, borderColor: pref ? "var(--accent)" : "var(--line)" }}>
                <div style={{ position: "relative" }}>
                  {c.url
                    ? <img src={c.url} alt={c.title} style={{ width: "100%", height: 200, objectFit: "cover", display: "block" }} />
                    : <Plate hue={c.hue} h={200} seed={i + 1} label={`0${i + 1}`} />}
                  {pref && <span className="badge accent" style={{ position: "absolute", top: 12, left: 12, background: "rgba(255,255,255,.92)" }}><Icon.check width={10} />Client's pick</span>}
                  {out && <span className="badge" style={{ position: "absolute", top: 12, left: 12, background: "rgba(255,255,255,.92)" }}>Ruled out</span>}
                </div>
                <div style={{ padding: 20, flex: 1, display: "flex", flexDirection: "column" }}>
                  <h3 className="serif" style={{ fontSize: 22 }}>{c.title}</h3>
                  {c.note && <p className="muted" style={{ marginTop: 8, fontSize: 14, lineHeight: 1.55 }}>{c.note}</p>}
                  {c.tags.length > 0 && (
                    <div className="row wrap" style={{ gap: 6, marginTop: 14 }}>
                      {c.tags.map((t) => <span key={t} className="badge">{t}</span>)}
                    </div>
                  )}
                  {c.response && (
                    <div className="panel" style={{ padding: "9px 12px", marginTop: 14, fontSize: 12.5 }}>
                      <span className="kicker" style={{ fontSize: 8.5 }}>{c.response.by} · {relative(c.response.on)}</span>
                      <div style={{ marginTop: 3 }}>{c.response.comment || (pref ? "Drawn to this one." : "Not this one.")}</div>
                    </div>
                  )}
                  {canEdit && (
                    <div className="row" style={{ gap: 6, marginTop: "auto", paddingTop: 14 }}>
                      <button className="chip" style={{ fontSize: 11 }} onClick={() => setEditing(c)}>Edit</button>
                      <button className="chip" style={{ fontSize: 11, marginLeft: "auto", color: "var(--danger)" }} onClick={() => actions.removeConcept(p.id, c.id)}>Remove</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </Grid>
      )}
      </>
      )}

      {editing && <ConceptModal pid={p.id} concept={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </Page>
  );
}

function ConceptModal({ pid, concept, onClose }: { pid: string; concept: ConceptBoard | null; onClose: () => void }) {
  const [title, setTitle] = useState(concept?.title ?? "");
  const [note, setNote] = useState(concept?.note ?? "");
  const [tags, setTags] = useState((concept?.tags ?? []).join(", "));
  const [url, setUrl] = useState<string | undefined>(concept?.url);
  const [busy, setBusy] = useState(false);

  async function pick(file?: File) {
    if (!file) return;
    setBusy(true);
    try { setUrl(await storeImage(await fileToDataUri(file))); } catch { /* keep what we had */ }
    setBusy(false);
  }
  function save() {
    if (!title.trim()) return;
    const list = tags.split(",").map((t) => t.trim()).filter(Boolean);
    if (concept) actions.updateConcept(pid, concept.id, { title: title.trim(), note: note.trim(), tags: list, url });
    else actions.addConcept(pid, { title, note, tags: list, url });
    onClose();
  }

  return (
    <Modal open onClose={onClose} title={concept ? "Edit direction" : "Add a concept direction"}>
      <div className="col" style={{ gap: 13 }}>
        <div className="field"><label>Title</label>
          <input className="input" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Quiet Monolith" />
        </div>
        <div className="field"><label>The thinking</label>
          <textarea className="input" rows={4} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="What this direction is about — the mood, the materials, how it handles light and the plan…" />
        </div>
        <div className="field"><label>Tags <span className="dim">(comma separated)</span></label>
          <input className="input" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="stone, restrained, top-lit" />
        </div>
        <div className="field"><label>Image <span className="dim">(optional)</span></label>
          {url && <img src={url} alt="" style={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 8, marginBottom: 8, border: "1px solid var(--line)" }} />}
          <label className="btn soft sm" style={{ cursor: "pointer", width: "fit-content" }}>
            <Icon.download width={14} style={{ transform: "rotate(180deg)" }} />{busy ? "Uploading…" : url ? "Replace image" : "Upload an image"}
            <input type="file" accept="image/*" hidden onChange={(e) => void pick(e.target.files?.[0] ?? undefined)} />
          </label>
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" onClick={save} disabled={!title.trim() || busy}><Icon.check width={15} />{concept ? "Save" : "Add direction"}</button>
        </div>
      </div>
    </Modal>
  );
}
